import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, TextField, MenuItem, Alert, CircularProgress, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { useSelector } from 'react-redux';

import { useAuth } from '../../providers/AuthProvider';
import { selectUserInfos } from '../../store/userDataStore';
import { apiTokenService, ApiToken, CreatedApiToken } from '../../services/apiTokenService';
import { userAdminService, AdminUser } from '../../services/userAdminService';

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleString('fr-FR') : '—');

const tokenStatus = (token: ApiToken): { label: string; color: 'success' | 'default' | 'warning' } => {
  if (!token.is_active) return { label: 'Révoqué', color: 'default' };
  if (token.is_expired) return { label: 'Expiré', color: 'warning' };
  return { label: 'Actif', color: 'success' };
};

// Les tokens d'API authentifient des services (runner GitLab, bot de sync Moodle) via un compte
// ci_runner. Le secret n'est jamais relisible : l'API ne le renvoie qu'à la création.
const AdminTokensPage: React.FC = () => {
  const { getValidToken } = useAuth();
  const userInfos = useSelector(selectUserInfos);
  const isAdmin = userInfos?.access === 'admin';

  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [serviceAccounts, setServiceAccounts] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [userLogin, setUserLogin] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('90');
  const [creating, setCreating] = useState(false);

  const [createdToken, setCreatedToken] = useState<CreatedApiToken | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokingId, setRevokingId] = useState<number | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      const [tokenList, users] = await Promise.all([apiTokenService.list(token), userAdminService.listUsers(token)]);
      setTokens(tokenList);
      setServiceAccounts(users.filter((u) => u.access === 'ci_runner'));
    } catch (e: any) {
      setError(e.message || 'Erreur lors du chargement des tokens');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  // userInfos est rechargé de façon asynchrone après un F5 : on attend avant de juger l'accès
  if (!userInfos) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Accès réservé aux administrateurs.</Alert>
      </Box>
    );
  }

  const accountLoginById = (userId: number) => serviceAccounts.find((u) => u.id === userId)?.login ?? `#${userId}`;

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      const days = parseInt(expiresInDays, 10);
      const created = await apiTokenService.create({
        name: name.trim(),
        description: description.trim() || undefined,
        user_login: userLogin,
        expires_in_days: Number.isNaN(days) || days <= 0 ? undefined : days,
      }, token);
      setCreatedToken(created);
      setCopied(false);
      setName('');
      setDescription('');
      await load();
    } catch (e: any) {
      setError(e.message || 'Erreur lors de la création du token');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (tokenId: number) => {
    if (!window.confirm('Révoquer ce token ? Les services qui l\'utilisent perdront leur accès.')) return;
    setRevokingId(tokenId);
    setError(null);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      await apiTokenService.revoke(tokenId, token);
      await load();
    } catch (e: any) {
      setError(e.message || 'Erreur lors de la révocation');
    } finally {
      setRevokingId(null);
    }
  };

  const handleCopy = async () => {
    if (!createdToken) return;
    try {
      await navigator.clipboard.writeText(createdToken.token);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h4">Tokens d'API</Typography>
      <Typography variant="body2" color="text.secondary">
        Ces tokens authentifient des services (runner GitLab, bot de synchronisation Moodle) au nom d'un compte
        de service (rôle ci_runner). Préférez une durée limitée : le secret n'est affiché qu'une fois, à la création.
      </Typography>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6">Nouveau token</Typography>
        {serviceAccounts.length === 0 && !loading && (
          <Alert severity="warning">
            Aucun compte de service (rôle ci_runner). Créez-en un dans « Admin Utilisateurs » avant de générer un token.
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField label="Nom" size="small" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Bot sync Moodle" sx={{ minWidth: 220 }} />
          <TextField label="Description" size="small" value={description}
            onChange={(e) => setDescription(e.target.value)} sx={{ minWidth: 260 }} />
          <TextField select label="Compte de service" size="small" value={userLogin}
            onChange={(e) => setUserLogin(e.target.value)} sx={{ minWidth: 200 }}>
            {serviceAccounts.map((u) => <MenuItem key={u.id} value={u.login}>{u.login}</MenuItem>)}
          </TextField>
          <TextField label="Durée (jours)" size="small" type="number" value={expiresInDays}
            onChange={(e) => setExpiresInDays(e.target.value)} helperText="Vide ou 0 = illimité" sx={{ width: 150 }} />
          <Button variant="contained" onClick={handleCreate}
            disabled={creating || !name.trim() || !userLogin}>
            {creating ? <CircularProgress size={20} /> : 'Générer'}
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nom</TableCell>
              <TableCell>Compte</TableCell>
              <TableCell>Préfixe</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Expire le</TableCell>
              <TableCell>Dernière utilisation</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {!loading && tokens.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center">Aucun token.</TableCell></TableRow>
            )}
            {tokens.map((t) => {
              const status = tokenStatus(t);
              return (
                <TableRow key={t.id}>
                  <TableCell>
                    {t.name}
                    {t.description && <Typography variant="caption" display="block" color="text.secondary">{t.description}</Typography>}
                  </TableCell>
                  <TableCell>{accountLoginById(t.user_id)}</TableCell>
                  <TableCell><code>{t.prefix}…</code></TableCell>
                  <TableCell><Chip size="small" label={status.label} color={status.color} /></TableCell>
                  <TableCell>{t.expires_at ? formatDate(t.expires_at) : 'Jamais'}</TableCell>
                  <TableCell>{formatDate(t.last_used_at)}</TableCell>
                  <TableCell align="right">
                    <Button size="small" color="error" disabled={!t.is_active || revokingId === t.id}
                      onClick={() => handleRevoke(t.id)}>Révoquer</Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={createdToken !== null} onClose={() => setCreatedToken(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Token créé</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="warning">Copiez-le maintenant : il ne sera plus jamais affiché.</Alert>
          <TextField value={createdToken?.token ?? ''} fullWidth slotProps={{ input: { readOnly: true } }}
            onFocus={(e) => e.target.select()} />
          <Typography variant="body2" color="text.secondary">
            Compte : {createdToken?.user_login} — expire : {createdToken?.expires_at ? formatDate(createdToken.expires_at) : 'jamais'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopy}>{copied ? 'Copié ✔' : 'Copier'}</Button>
          <Button variant="contained" onClick={() => setCreatedToken(null)}>J'ai noté le token</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminTokensPage;
